/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {LocationChangeListener, PlatformLocation} from '@angular/common';
import {Inject, Injectable} from '@angular/core';

import {getDOM} from '../../dom/dom_adapter';
import {DOCUMENT} from '../../dom/dom_tokens';

import {supportsState} from './history';
/**
 * `PlatformLocation` encapsulates all of the direct calls to platform APIs.
 * This class should not be used directly by an application developer. Instead, use
 * {\@link Location}.
 */
export class BrowserPlatformLocation extends PlatformLocation {
private _location: Location;
private _history: History;
/**
 * @param {?} _doc
 */
constructor(
private _doc: any) {
    super();
    this._init();
  }
/**
 * \@internal
 * @return {?}
 */
_init() {
    this._location = getDOM().getLocation();
    this._history = getDOM().getHistory();
  }
/**
 * @return {?}
 */
get location(): Location { return this._location; }
/**
 * @return {?}
 */
getBaseHrefFromDOM(): string { return /** @type {?} */(( getDOM().getBaseHref(this._doc))); }
/**
 * @param {?} fn
 * @return {?}
 */
onPopState(fn: LocationChangeListener): void {
    getDOM().getGlobalEventTarget(this._doc, 'window').addEventListener('popstate', fn, false);
  }
/**
 * @param {?} fn
 * @return {?}
 */
onHashChange(fn: LocationChangeListener): void {
    getDOM().getGlobalEventTarget(this._doc, 'window').addEventListener('hashchange', fn, false);
  }
/**
 * @return {?}
 */
get pathname(): string { return this._location.pathname; }
/**
 * @return {?}
 */
get search(): string { return this._location.search; }
/**
 * @return {?}
 */
get hash(): string { return this._location.hash; }
/**
 * @param {?} newPath
 * @return {?}
 */
set pathname(newPath: string) { this._location.pathname = newPath; }
/**
 * @param {?} state
 * @param {?} title
 * @param {?} url
 * @return {?}
 */
pushState(state: any, title: string, url: string): void {
    if (supportsState()) {
      this._history.pushState(state, title, url);
    } else {
      this._location.hash = url;
    }
  }
/**
 * @param {?} state
 * @param {?} title
 * @param {?} url
 * @return {?}
 */
replaceState(state: any, title: string, url: string): void {
    if (supportsState()) {
      this._history.replaceState(state, title, url);
    } else {
      this._location.hash = url;
    }
  }
/**
 * @return {?}
 */
forward(): void { this._history.forward(); }
/**
 * @return {?}
 */
back(): void { this._history.back(); }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Inject, args: [DOCUMENT, ] }, ]},
];
}

function BrowserPlatformLocation_tsickle_Closure_declarations() {
/** @type {?} */
BrowserPlatformLocation.decorators;
/**
 * @nocollapse
 * @type {?}
 */
BrowserPlatformLocation.ctorParameters;
/** @type {?} */
BrowserPlatformLocation.prototype._location;
/** @type {?} */
BrowserPlatformLocation.prototype._history;
/** @type {?} */
BrowserPlatformLocation.prototype._doc;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
